import { useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, FileText, PlusIcon, Sparkles, Wand2 } from "lucide-react";
import type { CampaignBriefV1 } from "@runcheetah/shared";

import { InfoTile, Notice, Select, Textarea } from "@/components/common";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { formatShortDate } from "@/lib/date-format";

import type { CampaignReviewPlannedPost } from "./types";
import {
  buildCampaignRunPrompt,
  getPlannedPostGenerationGuardrailMessage,
  getPlannedPostGenerationPanelState,
  getPostTypeLabel,
} from "./utils";

export function GeneratePlannedPostSheet({
  open,
  onOpenChange,
  triggerLabel,
  campaignName,
  campaignBrief,
  plannedPosts,
  selectedScheduledPostId,
  onSelectScheduledPostId,
  additionalDirection,
  onAdditionalDirectionChange,
  brandStatus,
  selectedScheduledPostHasActiveRun,
  lockSelection = false,
  disabled,
  isPending,
  errorMessage,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerLabel: string;
  campaignName: string;
  campaignBrief: CampaignBriefV1;
  plannedPosts: CampaignReviewPlannedPost[];
  selectedScheduledPostId: string | null;
  onSelectScheduledPostId: (id: string | null) => void;
  additionalDirection: string;
  onAdditionalDirectionChange: (value: string) => void;
  brandStatus: string;
  selectedScheduledPostHasActiveRun: boolean;
  lockSelection?: boolean;
  disabled: boolean;
  isPending: boolean;
  errorMessage?: string | null;
  onSubmit: () => void;
}) {
  const [briefPreviewOpen, setBriefPreviewOpen] = useState(false);
  const selectedScheduledPost =
    plannedPosts.find((post) => post.id === selectedScheduledPostId) ?? null;
  const generationPanelState = getPlannedPostGenerationPanelState({
    postStatus: selectedScheduledPost?.status ?? "draft",
    hasAttempt: (selectedScheduledPost?.attemptCount ?? 0) > 0,
    hasCompleted: Boolean(selectedScheduledPost?.output.latestCompleted),
  });
  const guardrailMessage = getPlannedPostGenerationGuardrailMessage({
    brandStatus,
    hasSelectedPost: Boolean(selectedScheduledPost),
    hasActiveRun: selectedScheduledPostHasActiveRun,
  });
  const briefPreview = useMemo(() => {
    if (!selectedScheduledPost) {
      return null;
    }

    return buildCampaignRunPrompt({
      campaignName,
      campaignSummary: campaignBrief.summary,
      goals: campaignBrief.goals.map((goal) => goal.label),
      slot: selectedScheduledPost.slot,
      additionalDirection,
    });
  }, [
    additionalDirection,
    campaignBrief.goals,
    campaignBrief.summary,
    campaignName,
    selectedScheduledPost,
  ]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button size="sm" className="gap-2">
          <PlusIcon className="size-4" />
          {triggerLabel}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] overflow-y-auto sm:w-[540px] sm:max-w-none">
        <SheetHeader>
          <SheetTitle>{generationPanelState.title}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          <div className="rounded-2xl border border-border/60 bg-muted/25 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-background p-2 shadow-sm">
                {generationPanelState.mode === "first_attempt" ? (
                  <Sparkles className="size-4 text-foreground/70" />
                ) : generationPanelState.mode === "regenerate" ? (
                  <Wand2 className="size-4 text-foreground/70" />
                ) : (
                  <ArrowRight className="size-4 text-foreground/70" />
                )}
              </div>
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-foreground/52">
                  Planned-post action
                </div>
                <p className="text-sm leading-7 text-foreground/78">
                  {generationPanelState.description}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="campaign-planned-post-select">
              Planned post
            </label>
            <Select
              id="campaign-planned-post-select"
              value={selectedScheduledPostId ?? ""}
              onChange={(event) => onSelectScheduledPostId(event.target.value || null)}
              disabled={lockSelection}
            >
              <option value="" disabled>
                Select a planned post
              </option>
              {plannedPosts.map((post) => (
                <option key={post.id} value={post.id}>
                  {formatShortDate(post.scheduledFor)} - {post.slot.concept}
                </option>
              ))}
            </Select>
            {selectedScheduledPost ? (
              <div className="rounded-2xl border border-border/60 bg-background/80 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-foreground/48">
                      Selected planned post
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground">
                      {selectedScheduledPost.slot.concept}
                    </div>
                  </div>
                  <Badge variant="outline" className="rounded-full text-[11px]">
                    {getPostTypeLabel(selectedScheduledPost.slot.postType)}
                  </Badge>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <SummaryTile
                    label="Objective"
                    value={
                      selectedScheduledPost.slot.objectiveLabel ??
                      selectedScheduledPost.slot.objective
                    }
                  />
                  <SummaryTile
                    label="Audience"
                    value={selectedScheduledPost.slot.audienceSegment ?? "General audience"}
                  />
                  <SummaryTile label="Campaign" value={campaignName} />
                  <SummaryTile
                    label="CTA"
                    value={selectedScheduledPost.slot.callToAction ?? "No CTA specified"}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {campaignBrief.goals.map((goal) => (
                    <Badge key={goal.label} variant="secondary" className="text-[11px]">
                      {goal.label}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="campaign-additional-direction">
              {generationPanelState.directionLabel}
            </label>
            <Textarea
              id="campaign-additional-direction"
              value={additionalDirection}
              onChange={(event) => onAdditionalDirectionChange(event.target.value)}
              rows={5}
              placeholder={generationPanelState.directionPlaceholder}
            />
            <p className="text-xs text-muted-foreground">
              Leave this blank if the existing planned-post strategy already says enough. The
              generation brief is assembled automatically from the campaign brief and the selected
              planned post.
            </p>
          </div>

          {briefPreview ? (
            <Collapsible open={briefPreviewOpen} onOpenChange={setBriefPreviewOpen}>
              <div className="rounded-2xl border border-border/60 bg-background/80 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-foreground/48">
                      Brief preview
                    </div>
                    <p className="mt-1 text-sm text-foreground/62">
                      Review the assembled generation brief only if you need to sanity-check the
                      instructions.
                    </p>
                  </div>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2 rounded-xl text-foreground/68"
                    >
                      <FileText className="size-4" />
                      {briefPreviewOpen ? "Hide" : "Preview"}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent className="pt-4">
                  <pre className="overflow-x-auto rounded-xl bg-muted/35 p-4 text-xs leading-6 text-foreground/78 whitespace-pre-wrap">
                    {briefPreview}
                  </pre>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ) : null}

          <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 size-4 text-foreground/65" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-foreground/48">
                  {generationPanelState.nextStepTitle}
                </div>
                <p className="mt-1 text-sm leading-6 text-foreground/68">
                  {generationPanelState.nextStepDescription}
                </p>
              </div>
            </div>
          </div>

          {guardrailMessage ? (
            <Notice
              tone={
                brandStatus !== "scanned"
                  ? "warning"
                  : selectedScheduledPostHasActiveRun
                    ? "info"
                    : "muted"
              }
            >
              {guardrailMessage}
            </Notice>
          ) : null}

          {errorMessage ? <Notice tone="danger">{errorMessage}</Notice> : null}

          <Button onClick={onSubmit} disabled={disabled} className="w-full">
            {isPending ? "Queueing generation attempt..." : generationPanelState.ctaLabel}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <InfoTile
      label={label}
      value={value}
      className="rounded-xl border-border/60 bg-muted/15 px-3 py-2.5"
      valueClassName="leading-6 text-foreground/78"
    />
  );
}
